import { useState, useEffect, useCallback } from 'react';
import { Shield, Save, Check, RefreshCw, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const CATEGORIES_MPR = {
  bleu:     { label: 'Bleu (Tres modeste)',  color: 'text-blue-400',   bg: 'bg-blue-500/20',   border: 'border-blue-500/40', dot: 'bg-blue-400' },
  jaune:    { label: 'Jaune (Modeste)',      color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/40', dot: 'bg-yellow-400' },
  violet:   { label: 'Violet (Intermediaire)', color: 'text-violet-400', bg: 'bg-violet-500/20', border: 'border-violet-500/40', dot: 'bg-violet-400' },
  rose:     { label: 'Rose (Superieur)',     color: 'text-pink-400',   bg: 'bg-pink-500/20',   border: 'border-pink-500/40', dot: 'bg-pink-400' },
  hors_mpa: { label: 'Hors MPA',            color: 'text-slate-400',  bg: 'bg-slate-500/20',  border: 'border-slate-500/40', dot: 'bg-slate-400' },
};

export default function MandataireANAHSection({ clientId, authUser }) {
  const [data, setData] = useState({ email_anah: '', mdp_anah: '', nom_amo: '', nom_mar: '', categorie_mpr: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showMdp, setShowMdp] = useState(false);
  const [amoOptions, setAmoOptions] = useState([]);
  const [marOptions, setMarOptions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mandataireRes, optionsRes] = await Promise.all([
        authFetch(`${API}/pipeline/client/row_${clientId}/mandataire-anah`),
        authFetch(`${API}/amo-mar/select-options`)
      ]);
      if (mandataireRes.ok) {
        const d = await mandataireRes.json();
        setData(prev => ({ ...prev, ...d.mandataire_anah }));
      }
      if (optionsRes.ok) {
        const o = await optionsRes.json();
        setAmoOptions(o.amo || []);
        setMarOptions(o.mar || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const update = (key, val) => { setData(prev => ({ ...prev, [key]: val })); setSaved(false); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/pipeline/client/row_${clientId}/mandataire-anah`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, user_email: authUser?.email || '' })
      });
      if (r.ok) setSaved(true);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center gap-2 text-slate-400">
          <RefreshCw className="w-4 h-4 animate-spin" /> Chargement...
        </div>
      </div>
    );
  }

  const catInfo = CATEGORIES_MPR[data.categorie_mpr] || null;

  return (
    <div className="bg-gradient-to-r from-slate-800/60 to-slate-900/60 border border-amber-500/20 rounded-xl p-4" data-testid="mandataire-anah-section">
      <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2 mb-4">
        <Shield className="w-4 h-4" />
        Informations Mandataire ANAH
      </h3>

      {/* Categorie MPR avec couleur */}
      <div className="mb-4">
        <label className="block text-xs text-slate-500 mb-1">Categorie MaPrimeRenov'</label>
        <div className="flex items-center gap-3">
          <select
            data-testid="mandataire-categorie"
            value={data.categorie_mpr || ''}
            onChange={e => update('categorie_mpr', e.target.value)}
            className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none appearance-none"
          >
            <option value="">Non renseigne</option>
            <option value="bleu">Bleu (Tres modeste)</option>
            <option value="jaune">Jaune (Modeste)</option>
            <option value="violet">Violet (Intermediaire)</option>
            <option value="rose">Rose (Superieur)</option>
            <option value="hors_mpa">Hors MPA</option>
          </select>
          {catInfo && (
            <div className={`px-3 py-1.5 rounded-lg text-sm font-medium ${catInfo.bg} ${catInfo.border} border ${catInfo.color}`} data-testid="categorie-badge">
              <span className={`inline-block w-2 h-2 rounded-full ${catInfo.dot} mr-1.5`} />
              {catInfo.label}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* AMO Select */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">AMO (Assistant Maitrise d'Ouvrage)</label>
          <select
            data-testid="mandataire-amo-select"
            value={data.nom_amo || ''}
            onChange={e => update('nom_amo', e.target.value)}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none appearance-none"
          >
            <option value="">-- Selectionner AMO --</option>
            {amoOptions.map(o => (
              <option key={o.id} value={o.nom}>{o.nom}</option>
            ))}
            {data.nom_amo && !amoOptions.find(o => o.nom === data.nom_amo) && (
              <option value={data.nom_amo}>{data.nom_amo} (legacy)</option>
            )}
          </select>
        </div>

        {/* MAR Select */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">MAR (Mandataire)</label>
          <select
            data-testid="mandataire-mar-select"
            value={data.nom_mar || ''}
            onChange={e => update('nom_mar', e.target.value)}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none appearance-none"
          >
            <option value="">-- Selectionner MAR --</option>
            {marOptions.map(o => (
              <option key={o.id} value={o.nom}>{o.nom}</option>
            ))}
            {data.nom_mar && !marOptions.find(o => o.nom === data.nom_mar) && (
              <option value={data.nom_mar}>{data.nom_mar} (legacy)</option>
            )}
          </select>
        </div>

        {/* Email ANAH */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Email MaPrimeRenov'</label>
          <div className="relative">
            <Mail className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-500" />
            <input
              data-testid="mandataire-email"
              value={data.email_anah || ''}
              onChange={e => update('email_anah', e.target.value)}
              placeholder="email@maprimerenov.fr"
              className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Mot de passe ANAH */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Mot de passe MaPrimeRenov'</label>
          <div className="relative">
            <Lock className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-500" />
            <input
              data-testid="mandataire-mdp"
              type={showMdp ? 'text' : 'password'}
              value={data.mdp_anah || ''}
              onChange={e => update('mdp_anah', e.target.value)}
              placeholder="********"
              className="w-full pl-9 pr-10 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
            />
            <button type="button" onClick={() => setShowMdp(!showMdp)} className="absolute right-3 top-2.5 text-slate-500 hover:text-white">
              {showMdp ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3 mt-4">
        <button onClick={save} disabled={saving} data-testid="mandataire-save-btn"
          className="px-6 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center gap-2 transition-colors">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        {saved && <span className="text-amber-400 text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Sauvegarde !</span>}
      </div>
    </div>
  );
}
